// @ts-check
import React from 'react';
import { useState, useCallback } from 'react';
import { Badge } from '../../components/Badge';
import { EVICTION_GRANT_COPY } from './evictionGrantCopy';

/** localStorage key for per-user hidden-column preferences. */
export const COLUMN_STORAGE_KEY = 'licenseTable.hiddenColumns.v1';

/** Columns hidden by default on narrow viewports (first visit only). */
const DENSE_HIDDEN_KEYS = ['tier', 'customerNumber', 'createdAt'];

/** Viewport width (px) below which dense columns are hidden by default. */
const DENSE_BREAKPOINT = 1280;

/**
 * Whether a license can be selected for QR-code export: active, has a serial,
 * not yet registered, and its registration code is unused.
 *
 * @param {{ active?: boolean, serialNumber?: string, registered?: boolean, registrationCodeUsed?: boolean }} license
 * @returns {boolean}
 */
export function isValidForQRCode(license) {
  return Boolean(
    license.active &&
      license.serialNumber &&
      !license.registered &&
      !license.registrationCodeUsed,
  );
}

/**
 * @typedef {Object} ColumnCtx
 * @property {(text: string) => React.ReactNode} highlightMatch
 */

/**
 * Toggleable data columns, in display order. Fixed structural columns
 * (row-select checkbox, Status, Actions) are rendered directly in index.jsx.
 * Each column owns its cell renderer so the table body stays data-driven and
 * the empty-state colSpan can be derived rather than hardcoded.
 *
 * @type {Array<{ key: string, label: string, dense: boolean, render: (lic: any, ctx: ColumnCtx) => React.ReactNode }>}
 */
export const COLUMNS = [
  {
    key: 'serialNumber',
    label: 'Serial Number',
    dense: false,
    render: (lic, ctx) => ctx.highlightMatch(lic.serialNumber),
  },
  {
    key: 'tier',
    label: 'Tier',
    dense: true,
    render: (lic) => lic.licenseTier || '-',
  },
  {
    key: 'deviceId',
    label: 'Device ID',
    dense: false,
    render: (lic) => lic.deviceId || '-',
  },
  {
    key: 'customerName',
    label: 'Customer Name',
    dense: false,
    render: (lic) => lic.customerName || '-',
  },
  {
    key: 'customerNumber',
    label: 'Customer Number',
    dense: true,
    render: (lic) => lic.customerNumber || '-',
  },
  {
    key: 'registered',
    label: 'Registered',
    dense: false,
    render: (lic) =>
      lic.registered
        ? `Yes (${new Date(lic.registrationTimestamp).toLocaleDateString()})`
        : 'No',
  },
  {
    key: 'qrUsed',
    label: 'QR Used',
    dense: false,
    render: (lic) =>
      lic.registrationCodeUsed ? (
        <Badge variant="success">Yes</Badge>
      ) : (
        <Badge variant="warning">No</Badge>
      ),
  },
  {
    key: 'createdAt',
    label: 'Created At',
    dense: true,
    render: (lic) => (lic.createdAt ? new Date(lic.createdAt).toLocaleString() : '-'),
  },
  {
    key: 'expiresAt',
    label: 'Expires At',
    dense: false,
    render: (lic) => (lic.expiresAt ? new Date(lic.expiresAt).toLocaleDateString() : 'Never'),
  },
  {
    key: 'daysLeft',
    label: 'Days Left',
    dense: false,
    render: (lic) => {
      if (!lic.expiresAt) return <span className="cell-muted">N/A</span>;
      const diffMs = new Date(lic.expiresAt) - new Date();
      const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      if (days <= 0) {
        return <Badge variant="warning">Expired ({days}d)</Badge>;
      }
      return <span className="cell-num">{days}d</span>;
    },
  },
  {
    // WP4 / G-2, item 6: the grant otherwise has no visible state anywhere in the table.
    // Absent or any non-boolean legacy value renders Off, matching the backend's own
    // strict `=== true` projection (never trust a truthy-but-not-boolean stored value).
    key: 'evictionGrant',
    label: EVICTION_GRANT_COPY.columnHeader,
    dense: true,
    render: (lic) => (lic.evictionGrant === true ? EVICTION_GRANT_COPY.onLabel : EVICTION_GRANT_COPY.offLabel),
  },
];

/** Column keys that are sortable, mapped to the license field they sort by. */
export const SORTABLE_KEYS = {
  serialNumber: 'serialNumber',
  customerName: 'customerName',
  customerNumber: 'customerNumber',
  createdAt: 'createdAt',
  expiresAt: 'expiresAt',
  daysLeft: 'expiresAt',
};

function readInitialHidden() {
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch {
    /* corrupt/unavailable storage — fall through to viewport default */
  }
  if (typeof window !== 'undefined' && window.innerWidth < DENSE_BREAKPOINT) {
    return new Set(DENSE_HIDDEN_KEYS);
  }
  return new Set();
}

/**
 * Per-user column visibility, persisted to localStorage. Filters/sort live in
 * the URL (a shareable concern); column choice is personal (per D3).
 */
export function useColumnVisibility() {
  const [hidden, setHidden] = useState(readInitialHidden);

  const toggle = useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      try {
        window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        /* storage unavailable — preference is session-only */
      }
      return next;
    });
  }, []);

  const isHidden = useCallback((key) => hidden.has(key), [hidden]);

  return { hidden, toggle, isHidden };
}
